import { useEffect, useState } from "react";
import { View, TextInput } from "react-native-web";
import { useLocation, useNavigate } from "react-router-dom";
import CustomText from "../components/atoms/CustomText";
import Button from "../components/common/Button";
import { colors, spacing } from "../utils/styling";
import { getSupabaseClient } from "../providers/supabase";

// Two-factor step for the advisor portal (compliance requirement, McKayla 2026-08-20).
// First time: enroll an authenticator app (QR code) and confirm a code.
// After that: enter the 6-digit code on each login.
// The backend refuses staff data unless the login's token is aal2 — this screen is how
// a session gets there. Lost phone: Tyler removes the factor in Supabase (Auth → Users).
type Mode = "loading" | "enroll" | "verify";

export default function MfaVerify() {
  const navigate = useNavigate();
  const location = useLocation();
  const next = (location.state as { next?: string } | null)?.next || "/advisor";

  const [mode, setMode] = useState<Mode>("loading");
  const [factorId, setFactorId] = useState<string>("");
  const [qrCode, setQrCode] = useState<string>("");
  const [secret, setSecret] = useState<string>("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const start = async () => {
      const supabase = getSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login", { replace: true });
        return;
      }
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aal?.currentLevel === "aal2") {
        navigate(next, { replace: true });
        return;
      }
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const verified = factors?.totp?.find((f) => f.status === "verified");
      if (verified) {
        setFactorId(verified.id);
        setMode("verify");
        return;
      }
      // Clear any half-finished enrollment from an earlier attempt, then start fresh.
      for (const f of factors?.all || []) {
        if (f.status !== "verified") {
          await supabase.auth.mfa.unenroll({ factorId: f.id });
        }
      }
      const { data, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: "totp",
        friendlyName: "Money Personality",
      });
      if (enrollError || !data) {
        setError("Could not start two-factor setup. Please refresh and try again.");
        setMode("enroll");
        return;
      }
      setFactorId(data.id);
      setQrCode(data.totp.qr_code);
      setSecret(data.totp.secret);
      setMode("enroll");
    };
    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVerify = async () => {
    setError("");
    const clean = code.replace(/\s/g, "");
    if (!/^\d{6}$/.test(clean)) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setBusy(true);
    const { error: verifyError } = await getSupabaseClient().auth.mfa.challengeAndVerify({
      factorId,
      code: clean,
    });
    setBusy(false);
    if (verifyError) {
      setError("That code didn't work. Codes change every 30 seconds, so try the current one.");
      return;
    }
    navigate(next, { replace: true });
  };

  const cancel = async () => {
    await getSupabaseClient().auth.signOut();
    navigate("/login", { replace: true });
  };

  return (
    <View style={{ flex: 1, alignItems: "center", justifyContent: "center", padding: spacing.xl, minHeight: "100vh" }}>
      <View style={{ width: "100%", maxWidth: 420 }}>
        <CustomText style={{ fontSize: 28, fontFamily: "Nunito", fontWeight: "700", marginBottom: spacing.sm }}>
          Two-factor sign in
        </CustomText>

        {mode === "loading" && <CustomText style={{ fontFamily: "Nunito" }}>Loading...</CustomText>}

        {mode === "enroll" && (
          <View>
            <CustomText style={{ fontFamily: "Nunito", color: colors.gray[600], marginBottom: spacing.md }}>
              The advisor portal requires two-factor sign in. Scan this code with an authenticator
              app (Google Authenticator, Authy, or 1Password), then enter the 6-digit code it shows.
            </CustomText>
            {qrCode ? (
              <img src={qrCode} alt="Two-factor QR code" style={{ width: 200, height: 200, alignSelf: "center" }} />
            ) : null}
            {secret ? (
              <CustomText style={{ fontFamily: "Nunito", fontSize: 13, color: colors.gray[600], marginTop: spacing.sm }}>
                Can't scan? Enter this key in the app: {secret}
              </CustomText>
            ) : null}
          </View>
        )}

        {mode === "verify" && (
          <CustomText style={{ fontFamily: "Nunito", color: colors.gray[600], marginBottom: spacing.md }}>
            Enter the 6-digit code from your authenticator app.
          </CustomText>
        )}

        {mode !== "loading" && (
          <View style={{ marginTop: spacing.md }}>
            <TextInput
              value={code}
              onChangeText={setCode}
              placeholder="123456"
              placeholderTextColor={colors.gray[500]}
              keyboardType="number-pad"
              onSubmitEditing={handleVerify}
              style={{
                borderWidth: 1,
                borderColor: colors.gray[200],
                borderRadius: 6,
                paddingHorizontal: 12,
                paddingVertical: 10,
                fontSize: 18,
                letterSpacing: 4,
                fontFamily: "Nunito",
                marginBottom: spacing.sm,
              }}
            />
            {error ? (
              <CustomText style={{ color: colors.danger, fontFamily: "Nunito", marginBottom: spacing.sm }}>
                {error}
              </CustomText>
            ) : null}
            <Button onPress={handleVerify} label="Verify" loading={busy} disabled={busy} />
            <View style={{ marginTop: spacing.md }}>
              <Button onPress={cancel} label="Cancel and sign out" />
            </View>
          </View>
        )}
      </View>
    </View>
  );
}
